import { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { gql } from '@apollo/client';
import ProductImages from '../Product/ProductImages';
import ProductTitles from '../Product/ProductTitles';
import Translate from '../Translate/Translate';
import { Tooltip } from 'react-tooltip';
import FavouriteIcon from '../Favourite/FavouriteIcon';
import Alert, { Type } from '../Alert';
import ProductModelInfo from '../Product/ProductModelInfo';
import Price, { PriceType } from '../Price/Price';
import DiscountPercentage from '../Price/DiscountPercentage';
import useStores from '../../hooks/useStores';
import LocalizedLink from '../LocalizedLink';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { useMutation, useQuery } from '@apollo/client/react';
import { isDev } from '../../services/Env';

const GET_PRODUCT = gql`
  query Product($slug: String!) {
    product(slug: $slug) {
      id
      title
      slug
      brand {
        title
      }
      categories {
        longTitle
      }
      modelInfo {
        height
        size
      }
      prices {
        type
        value
      }
      isNew
      variations {
        id
        value
        code
      }
      media {
        id
        urls {
          W100
          W800
        }
      }
      defaultPrice
    }
  }
`;

export type ProductData = {
  id: string;
  title: string;
  slug: string;
  brand: {
    title: string;
  };
  categories: {
    longTitle: string;
  }[];
  modelInfo: {
    height: string;
    size: string;
  };
  prices: {
    type: PriceType;
    value: number;
  }[];
  isNew: boolean;
  variations: {
    id: string;
    value: string;
    code: string;
  }[];
  media: {
    id: string;
    urls: {
      W100: string;
      W800: string;
    };
  }[];
  defaultPrice: number;
};

type ProductResponseData = {
  product: ProductData;
};

interface Props {
  productSlug: string;
  urlQuery?: string;
}

const ADD_TO_CART = gql`
  mutation AddToCart($cartToken: String!, $variationCode: String!) {
    addToCart(cartToken: $cartToken, variationCode: $variationCode)
  }
`;

export default function QuickView({ productSlug, urlQuery }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentVariation, setCurrentVariation] = useState('');
  const [buyConfirmation, setBuyConfirmation] = useState('');
  const [isVariationWarningVisible, setIsVariationWarningVisible] =
    useState(false);
  const { cart } = useStores();
  const [addToCart] = useMutation(ADD_TO_CART);

  const handleAddToCart = async () => {
    await addToCart({
      variables: { cartToken: cart.token, variationCode: currentVariation },
    });
    await cart.load();
  };

  async function addCurrentVariationToCart() {
    if (!currentVariation) {
      setIsVariationWarningVisible(true);
      setTimeout(() => setIsVariationWarningVisible(false), 3000);
      return;
    }
    try {
      await handleAddToCart();

      if (isDev()) {
        const quantity = 1;

        const prices = product?.prices ?? [];
        const cartPrices = prices.filter(
          (price) =>
            price.type !== PriceType.ClientExtra &&
            price.type !== PriceType.Client,
        );

        const finalPrice = cartPrices.length
          ? Math.min(...cartPrices.map((price) => price.value)) * quantity
          : 0;

        const discountAmount = product?.defaultPrice - finalPrice;

        const selectedVariation = product?.variations.find(
          (variation) => variation.code === currentVariation,
        );

        // @ts-ignore
        if (dataLayer && product) {
          // @ts-ignore
          dataLayer.push({ ecommerce: null });

          // @ts-ignore
          dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'EUR',
              value: finalPrice,
              items: [
                {
                  item_id: product.id,
                  item_name: `${product.brand.title} ${product.title}`,
                  item_brand: product.brand.title,
                  item_category: product.categories?.[0]?.longTitle,
                  quantity: quantity,
                  price: product.defaultPrice,
                  discount: Number(discountAmount.toFixed(2)),
                  item_variant: selectedVariation
                    ? selectedVariation.value
                    : undefined,
                },
              ],
            },
          });
        }
      }
      setBuyConfirmation('true');
      setTimeout(() => {
        setCurrentVariation('');
      }, 1000);
    } catch (error) {
      console.error('Error adding to cart', error);
      setBuyConfirmation('false');
    }
  }

  function closeModal() {
    setIsOpen(false);
    setCurrentVariation('');
  }

  function openModal() {
    setIsOpen(true);
  }

  const { data } = useQuery<ProductResponseData>(GET_PRODUCT, {
    variables: { slug: productSlug },
    skip: !isOpen || !productSlug,
  });

  const product = data?.product;

  const brandName = product?.brand?.title || '';
  const productName = product?.title || '';
  const mainDisplayTitle = `${brandName} ${productName}`;

  let categoryDisplayTitle = '';
  if (product?.categories && product.categories.length > 0) {
    categoryDisplayTitle = product.categories
      .map((category) => category.longTitle)
      .join(', ');
  }

  let sizes = null;
  let productCode;
  if (product?.variations.length) {
    sizes = product.variations.map((size) => {
      let className =
        'items-center rounded cursor-pointer flex flex-row text-base justify-center transition-[ease] duration-100 mr-2.5 mb-2.5 px-3.75 py-2.5 border-2 border-solid border-[#eeeeee] hover:text-white hover:transition-[ease] hover:duration-100 hover:border-2 hover:border-solid hover:border-black hover:bg-black';

      if (currentVariation === size.code) {
        className += 'border-2 border-solid border-black bg-black !text-white';
        productCode = (
          <div
            className="relative inline-block cursor-pointer ml-auto"
            data-tooltip-id="variation"
            data-tooltip-content={currentVariation}
          >
            <p className="text-xs text-rgrey">
              <Translate component="product" keyword="code-tooltip" />
            </p>
            <Tooltip id="variation" clickable />
          </div>
        );
      }

      return (
        <button
          className={className}
          key={size.code}
          onClick={() => setCurrentVariation(size.code)}
          aria-label={`Suurus: ${size.value}`}
        >
          {size.value}
        </button>
      );
    });
  }

  useEffect(() => {
    if (buyConfirmation === 'true' || buyConfirmation === 'false') {
      const t = setTimeout(() => setBuyConfirmation(''), 4000);
      return () => clearTimeout(t);
    }
  }, [buyConfirmation]);

  let buyConfirmMessage = null;
  if (buyConfirmation === 'true') {
    buyConfirmMessage = (
      <Alert show type={Type.SUCCESS}>
        <Translate component="product" keyword="add-to-cart-ok" />
      </Alert>
    );
  } else if (buyConfirmation === 'false') {
    buyConfirmMessage = (
      <Alert show type={Type.ERROR}>
        <Translate component="product" keyword="add-to-cart-failed" />
      </Alert>
    );
  }

  return (
    <div className="hidden md:block">
      <button
        onClick={openModal}
        className="rounded-full bg-rgrey-middle p-1.5 cursor-pointer"
        aria-label="Kiirvaade"
        title="Kiirvaade"
      >
        <EyeIcon className="h-5 w-5 text-black" />
      </button>

      <Dialog open={isOpen} onClose={closeModal} className="relative z-9999">
        <DialogBackdrop
          transition
          className="fixed inset-0 hidden bg-black/60 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in md:block"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-stretch justify-center text-center md:items-center md:px-2 lg:px-4">
            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              aria-hidden="true"
              className="hidden md:inline-block md:h-screen md:align-middle"
            >
              &#8203;
            </span>
            <DialogPanel
              transition
              className="flex w-full transform text-left text-base transition data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in md:my-8 md:max-w-3xl md:px-4 data-closed:md:translate-y-0 data-closed:md:scale-95 lg:max-w-4xl"
            >
              <div className="relative flex w-full items-center overflow-hidden bg-white px-4 pt-14 pb-8 shadow-2xl sm:px-6 sm:pt-8 md:p-6 lg:p-8 rounded">
                <button
                  type="button"
                  onClick={closeModal}
                  className="absolute z-10 top-4 right-4 text-rgrey-dark hover:text-rgrey-darker sm:top-8 sm:right-6 md:top-6 md:right-6 lg:top-8 lg:right-8"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className="size-6 cursor-pointer"
                  />
                </button>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="w-full">
                    <ProductImages productSlug={product?.slug} />
                  </div>
                  <div>
                    <div className="pt-5 md:pt-0">
                      <DiscountPercentage
                        prices={product?.prices}
                        productNew={product?.isNew}
                        componentClassName="relative"
                        containerClassName="items-start p-0 mb-2"
                      />
                      <ProductTitles
                        title={mainDisplayTitle}
                        categoryTitle={categoryDisplayTitle}
                        titleClassName="block text-2xl leading-9 font-axi-bold"
                        spanClassName="text-rgrey-text font-axi text-sm"
                      />

                      <div className="mb-4">
                        <div className="flex items-center gap-3 mx-0 my-5">
                          <Price
                            prices={product?.prices}
                            priceLabel={true}
                            productNew={product?.isNew}
                            discountPriceClassname="text-3xl mr-3"
                            defaultPriceClassName="text-xl"
                            discountLabelClassname="text-xs"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 mb-2.5">
                      <div className="flex items-center justify-between mb-2.5">
                        <h5 className="text-sm font-axi-bold mr-5">
                          <Translate
                            component="product"
                            keyword="choose-size"
                          />
                        </h5>
                        {productCode}
                      </div>
                      <div className="flex flex-wrap">{sizes}</div>
                    </div>
                    {product?.modelInfo ? (
                      <ProductModelInfo modelInfo={product.modelInfo} />
                    ) : null}
                    <div>
                      <Alert
                        show={isVariationWarningVisible}
                        type={Type.WARNING}
                      >
                        <Translate component="product" keyword="choose-size" />
                      </Alert>

                      {buyConfirmMessage}
                    </div>

                    <div className="flex items-center mt-5 p-0">
                      <FavouriteIcon
                        type="button"
                        productSlug={product?.slug}
                        componentClassName="relative border rounded-sm mr-2.5 px-2 py-1.5 border-solid border-rgrey-light"
                      />

                      <button
                        className={`flex items-center justify-center rounded text-white text-sm flex-auto cursor-pointer px-4 py-4.25 bg-rgreen font-axi-bold ${
                          currentVariation !== ''
                            ? 'text-white opacity-100 cursor-pointer'
                            : ''
                        }`}
                        onClick={addCurrentVariationToCart}
                      >
                        <Translate component="product" keyword="add-to-cart" />
                      </button>
                    </div>
                    <div className="flex my-6 justify-end">
                      <LocalizedLink
                        to={`/toode/${product?.slug}${urlQuery}`}
                        className="flex cursor-pointer hover:underline items-center"
                      >
                        <p className="text-sm text-black">
                          Vaata rohkem detaile
                        </p>
                        <ArrowRightIcon className="size-4 ml-3 text-black" />
                      </LocalizedLink>
                    </div>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
